import { useEffect, useState } from "react";
import { X, Loader2, Download, FileWarning } from "lucide-react";
import mammoth from "mammoth/mammoth.browser";
import DOMPurify from "dompurify";

export default function DocumentViewer({ doc, canDownload, onClose }) {
  const [status, setStatus] = useState("loading");
  const [html, setHtml] = useState("");
  const [blobUrl, setBlobUrl] = useState("");
  const name = (doc.file_name || doc.doc_type || "").toLowerCase();
  const isImg = (doc.file || "").startsWith("data:image/") || /\.(png|jpe?g|gif|webp|bmp|heic|heif|svg)$/.test(name);
  const isPdf = (doc.file || "").startsWith("data:application/pdf") || name.endsWith(".pdf");
  const isDocx = name.endsWith(".docx");

  useEffect(() => {
    let created;
    (async () => {
      try {
        if (isImg) { setStatus("image"); return; }
        if (isPdf) {
          const b = await (await fetch(doc.file)).blob();
          const url = URL.createObjectURL(b); created = url; setBlobUrl(url); setStatus("pdf"); return;
        }
        if (isDocx) {
          const ab = await (await fetch(doc.file)).arrayBuffer();
          const res = await mammoth.convertToHtml({ arrayBuffer: ab });
          setHtml(DOMPurify.sanitize(res.value || "<p>(empty document)</p>")); setStatus("docx"); return;
        }
        setStatus("unsupported");
      } catch (e) { setStatus("unsupported"); }
    })();
    return () => { if (created) URL.revokeObjectURL(created); };
  }, [doc, isImg, isPdf, isDocx]);

  const download = () => { const a = document.createElement("a"); a.href = doc.file; a.download = doc.file_name || doc.doc_type; a.click(); };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-slate-900/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-4xl h-[88vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="document-viewer">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200 bg-slate-50">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{doc.doc_type}</p>
            <p className="text-xs text-slate-500 font-mono truncate">{doc.file_name}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canDownload && (
              <button onClick={download} data-testid="viewer-download-button" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-white">
                <Download className="h-3.5 w-3.5" /> Download
              </button>
            )}
            <button onClick={onClose} data-testid="viewer-close-button" className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-auto bg-slate-100">
          {status === "loading" && <div className="h-full flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div>}
          {status === "image" && <div className="h-full flex items-center justify-center p-4"><img src={doc.file} alt={doc.doc_type} className="max-w-full max-h-full object-contain" /></div>}
          {status === "pdf" && <iframe title="pdf" src={`${blobUrl}#toolbar=0&navpanes=0`} className="w-full h-full border-0" />}
          {status === "docx" && <div className="max-w-3xl mx-auto bg-white my-6 p-8 shadow-sm rounded-lg prose prose-slate prose-sm" dangerouslySetInnerHTML={{ __html: html }} />}
          {status === "unsupported" && (
            <div className="h-full flex flex-col items-center justify-center text-slate-500 gap-3 p-6 text-center">
              <FileWarning className="h-10 w-10 text-amber-500" />
              <p className="text-sm">Preview not available for this file type.</p>
              {canDownload && <button onClick={download} className="text-sm text-emerald-700 hover:underline">Download instead</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
